import { LensTheme, StatusIndicator } from '@diligentcorp/atlas-theme-mui-lens';
import {
  Avatar,
  AvatarGroup,
  Box,
  CircularProgress,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  useTheme,
} from '@mui/material';
import React from 'react';
import { ColumnType } from 'src/components/molecules';
import { OnRowClickType } from 'src/components/molecules/Table';
import { ReportStatus } from 'src/graphql';
import { capitaliseFirstLetter } from 'src/utils';
import { getInitials } from 'src/utils/getInitials';
import { SpeakUpStatusColorMap } from '../helpers/SpeakUpColorMap';
import * as styles from './SpeakUpTable.styles';

export interface RowModel {
  caseName: string;
  caseReadableId: string;
  caseId: string;
  caseTags: string[] | string;
  category: string;
  createdDate: string;
  addedDate: string;
  lastUpdate: string;
  status: string;
  caseLead: string;
  caseTeam: string[];
  assignCaseLead: React.JSX.Element | null;
  caseLinks: number;
  source: string | undefined;
  data?: {
    status: ReportStatus;
    reportId: string;
    existingReport: boolean;
  };
}

export interface SpeakUpTableProps {
  'data-testid'?: string;
  columns: ColumnType[];
  rows: { tableRowData: RowModel }[];
  isLoading: boolean;
  isError: boolean;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onRowClick: OnRowClickType<{
    status: ReportStatus;
    reportId: string;
    existingReport: boolean;
  }>;
  emptyState: React.ReactNode;
  caseLinkingEnabled: boolean;
  sortableColumns: string[];
  sortedColumn?: { columnName: string; direction: 'asc' | 'desc' };
  sortColumns?: (columnName: string, direction: 'asc' | 'desc') => void;
}

export const SpeakUpTable: React.FC<SpeakUpTableProps> = ({
  'data-testid': dataTestId,
  columns,
  rows,
  isLoading,
  isError,
  page,
  totalPages,
  onPageChange,
  onRowClick,
  emptyState,
  caseLinkingEnabled,
  sortableColumns,
  sortedColumn,
  sortColumns,
}) => {
  const { presets } = useTheme<LensTheme>();
  const { getAvatarProps } = presets.AvatarPresets;
  const AtlasTableSortLabel =
    presets.TablePresets.components.AtlasTableSortLabel;

  if (isLoading) {
    return (
      <Box
        data-testid={
          dataTestId ? `${dataTestId}-loading` : 'disclosures-table-loading'
        }
        sx={styles.loading}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !rows.length) {
    return <Box sx={styles.emptyWrapper}>{emptyState}</Box>;
  }

  return (
    <Box data-testid={dataTestId}>
      <TableContainer sx={styles.tableContainer}>
        <Table sx={styles.table}>
          <TableHead>
            <TableRow sx={styles.headerRow}>
              {columns.map((col) =>
                sortableColumns.includes(col.label) ? (
                  <TableCell
                    key={col.value}
                    sx={styles.headerCell}
                    sortDirection={
                      sortedColumn?.columnName === col.label
                        ? sortedColumn.direction
                        : false
                    }
                  >
                    <AtlasTableSortLabel
                      active={sortedColumn?.columnName === col.label}
                      direction={
                        sortedColumn?.columnName === col.label
                          ? sortedColumn?.direction
                          : 'asc'
                      }
                      onClick={() => {
                        if (sortColumns) {
                          const newDirection =
                            sortedColumn?.columnName === col.label &&
                            sortedColumn?.direction === 'asc'
                              ? 'desc'
                              : 'asc';
                          sortColumns(col.label, newDirection);
                        }
                      }}
                      sortedAscendingLabel="Sorted ascending"
                      sortedDescendingLabel="Sorted descending"
                    >
                      {col.label}
                    </AtlasTableSortLabel>
                  </TableCell>
                ) : (
                  <TableCell key={col.value} sx={styles.headerCell}>
                    {col.label}
                  </TableCell>
                ),
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const data = row.tableRowData;
              const initials = data.caseLead
                ? getInitials(data.caseLead)
                : undefined;
              const category =
                data.category.length > 20
                  ? `${data.category.substring(0, 20)}...`
                  : data.category;
              return (
                <TableRow
                  key={data.caseId}
                  hover
                  data-testid={`case-row-${data.caseId}`}
                  onClick={(e) => onRowClick(data.caseId, data.data, e)}
                  sx={styles.row}
                >
                  <TableCell sx={styles.cell}>
                    <StatusIndicator
                      label={capitaliseFirstLetter(data.status)}
                      customColor={SpeakUpStatusColorMap(data.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={styles.cell}>
                    <Box component="span" sx={styles.caseName}>
                      {data.caseName}
                    </Box>
                  </TableCell>
                  <TableCell sx={styles.cell}>{data.caseReadableId}</TableCell>
                  <TableCell sx={styles.cell}>{data.caseTags}</TableCell>
                  <TableCell sx={styles.cell}>
                    <Tooltip key={data.category} title={data.category}>
                      <Box component="span">{category}</Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={styles.cell}>{data.addedDate}</TableCell>
                  <TableCell sx={styles.cell}>{data.createdDate}</TableCell>
                  <TableCell sx={styles.cell}>{data.lastUpdate}</TableCell>
                  <TableCell
                    sx={{ ...styles.caseLadCell }}
                    data-testid="cell-Assigned Case Lead"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar
                        {...getAvatarProps({ size: 'medium', color: 'red' })}
                      >
                        {initials}
                      </Avatar>
                      {data.assignCaseLead}
                    </Box>
                  </TableCell>

                  <TableCell sx={styles.cell}>
                    <AvatarGroup
                      max={3}
                      sx={{ display: 'flex', flexDirection: 'row-reverse' }}
                      slotProps={{
                        surplus: {
                          sx: { ...getAvatarProps({ size: 'medium' }).sx },
                        },
                      }}
                    >
                      {data?.caseTeam?.map((member) => (
                        <Tooltip key={member} title={member}>
                          <Avatar {...getAvatarProps({ size: 'medium' })}>
                            {getInitials(member)}
                          </Avatar>
                        </Tooltip>
                      ))}
                    </AvatarGroup>
                  </TableCell>
                  {caseLinkingEnabled && (
                    <TableCell sx={styles.cell}>{data.caseLinks}</TableCell>
                  )}
                  <TableCell sx={styles.cell}>{data.source}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={styles.paginationRow}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(_, value) => onPageChange(value)}
        />
      </Box>
    </Box>
  );
};
